// Integrations list + create/delete UI for the service detail page.
// Admins-only; members see a placeholder note. The newly-created
// integration's secret is highlighted at the top so the admin can copy
// it into the provider — Firestore stores it too, but the visual cue
// is "act on this now".

import { useEffect, useState } from 'react';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { Copy, Plug, Plus, Trash2 } from 'lucide-react';
import { orgIntegrationsCol } from '../../lib/firestore';
import {
  PROVIDER_LABELS,
  createIntegration,
  deleteIntegration,
  readIntegration,
  webhookUrl,
} from '../../lib/integrations';
import type { Integration, IntegrationProvider } from '../../lib/types';
import {
  Badge,
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Row,
  Spinner,
  Stack,
} from '../ui';
import { ErrorBanner } from '../../pages/SigninPage';

const PROVIDERS: IntegrationProvider[] = [
  'datadog',
  'newrelic',
  'sentry',
  'dynatrace',
  'pagerduty',
  'generic',
];

export function IntegrationsCard({
  orgId,
  serviceId,
  isAdmin,
}: {
  orgId: string;
  serviceId: string;
  isAdmin: boolean;
}) {
  const [integrations, setIntegrations] = useState<Integration[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create form state.
  const [showForm, setShowForm] = useState(false);
  const [provider, setProvider] = useState<IntegrationProvider>('datadog');
  const [name, setName] = useState('');
  // Sentry only — admin pastes the Client Secret from Sentry's Internal
  // Integration UI. Other providers leave this empty; Rouser generates.
  const [sentrySecret, setSentrySecret] = useState('');
  const [creating, setCreating] = useState(false);

  // Most-recently-created secret + its URL — surfaced once in a copy
  // panel so the admin can paste it into the provider config.
  const [freshlyCreated, setFreshlyCreated] = useState<{
    integrationId: string;
    provider: IntegrationProvider;
    secret: string;
  } | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(
      query(orgIntegrationsCol(orgId), orderBy('createdAt', 'asc')),
      (snap) => {
        setIntegrations(
          snap.docs
            .map((d) => readIntegration(d.data() as Parameters<typeof readIntegration>[0]))
            .filter((i) => i.serviceId === serviceId),
        );
      },
      (err) => {
        console.error('[integrations] snapshot failed', err);
        setError('Could not load integrations.');
      },
    );
    return () => unsub();
  }, [orgId, serviceId, isAdmin]);

  if (!isAdmin) {
    return (
      <p className="ds-text-muted ds-text-sm">
        Inbound webhook configurations from Datadog, New Relic, etc. Admins manage these.
      </p>
    );
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (creating || !name.trim()) return;
    if (provider === 'sentry' && !sentrySecret.trim()) {
      setError('Paste the Sentry Client Secret first.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const { integrationId, secret } = await createIntegration(orgId, {
        serviceId,
        provider,
        name: name.trim(),
        secret: provider === 'sentry' ? sentrySecret.trim() : undefined,
      });
      setFreshlyCreated({ integrationId, provider, secret });
      setName('');
      setSentrySecret('');
      setShowForm(false);
    } catch (err) {
      console.error('[integrations] create failed', err);
      setError(extractMessage(err));
    } finally {
      setCreating(false);
    }
  }

  async function confirmDelete() {
    if (!confirmDeleteId || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteIntegration(orgId, confirmDeleteId);
      if (freshlyCreated?.integrationId === confirmDeleteId) {
        setFreshlyCreated(null);
      }
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('[integrations] delete failed', err);
      setError(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Stack gap={4}>
      {error && <ErrorBanner message={error} />}

      {freshlyCreated && (
        <FreshSecretPanel
          orgId={orgId}
          integrationId={freshlyCreated.integrationId}
          provider={freshlyCreated.provider}
          secret={freshlyCreated.secret}
          onDismiss={() => setFreshlyCreated(null)}
        />
      )}

      {showForm ? (
        <Stack gap={4} as="form" onSubmit={submitCreate}>
          <Row gap={3} align="center">
            <Plug size={16} strokeWidth={1.75} color="var(--color-accent)" />
            <span style={{ fontWeight: 'var(--fw-medium)' }}>New integration</span>
          </Row>
          <FormField label="Provider">
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={provider}
                onChange={(e) => setProvider(e.target.value as IntegrationProvider)}
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {PROVIDER_LABELS[p]}
                  </option>
                ))}
              </select>
            )}
          </FormField>
          <FormField label="Name" hint="Display name only — e.g. 'Datadog prod'.">
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Datadog prod"
                required
                maxLength={80}
                autoFocus
              />
            )}
          </FormField>
          {provider === 'sentry' && (
            <FormField
              label="Sentry Client Secret"
              hint="Create an Internal Integration in Sentry first (Settings → Custom Integrations → Create New Integration). After saving, copy the generated Client Secret and paste it here. Rouser uses it to verify HMAC signatures on incoming webhooks."
            >
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  value={sentrySecret}
                  onChange={(e) => setSentrySecret(e.target.value)}
                  placeholder="•••• Sentry Client Secret"
                  required
                />
              )}
            </FormField>
          )}
          <Row gap={2} justify="end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowForm(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!name.trim()} loading={creating}>
              Create integration
            </Button>
          </Row>
        </Stack>
      ) : (
        <Row justify="end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowForm(true)}
            iconLeft={<Plus size={12} strokeWidth={2} />}
          >
            Add integration
          </Button>
        </Row>
      )}

      {integrations === null ? (
        <Row gap={2} align="center">
          <Spinner size="sm" />
          <span className="ds-text-muted ds-text-sm">Loading…</span>
        </Row>
      ) : integrations.length === 0 ? (
        <p className="ds-text-muted ds-text-sm">
          No integrations yet. Add one and Rouser will give you a webhook URL to paste into the
          provider.
        </p>
      ) : (
        <Stack gap={2}>
          {integrations.map((i) => (
            <IntegrationRow
              key={i.id}
              integration={i}
              onDelete={() => setConfirmDeleteId(i.id)}
            />
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
        title="Delete integration?"
        confirmLabel="Delete"
        destructive
        loading={deleting}
      >
        <p>
          The webhook URL will stop working immediately. Any provider still posting to it will
          start getting 401s — remember to remove or rotate it on the provider side too.
        </p>
      </ConfirmDialog>
    </Stack>
  );
}

function IntegrationRow({
  integration,
  onDelete,
}: {
  integration: Integration;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        alignItems: 'center',
      }}
    >
      <Stack gap={1}>
        <Row gap={2} align="center">
          <Badge tone="accent">{PROVIDER_LABELS[integration.provider]}</Badge>
          <span style={{ fontWeight: 'var(--fw-medium)' }}>{integration.name}</span>
        </Row>
        <span className="ds-text-tertiary ds-text-xs">
          {integration.lastUsedAt
            ? `Last fired ${new Date(integration.lastUsedAt).toLocaleString()}`
            : 'No events received yet'}
        </span>
      </Stack>
      <Button
        variant="danger-ghost"
        size="sm"
        iconLeft={<Trash2 size={12} strokeWidth={2} />}
        onClick={onDelete}
      >
        Delete
      </Button>
    </div>
  );
}

function FreshSecretPanel({
  orgId,
  integrationId,
  provider,
  secret,
  onDismiss,
}: {
  orgId: string;
  integrationId: string;
  provider: IntegrationProvider;
  secret: string;
  onDismiss: () => void;
}) {
  const url = webhookUrl(provider, orgId, integrationId);
  const [urlCopied, setUrlCopied] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  function copy(text: string, set: (b: boolean) => void) {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        set(true);
        setTimeout(() => set(false), 1500);
      })
      .catch(() => undefined);
  }

  return (
    <div
      style={{
        background: 'var(--color-accent-soft)',
        border: '1px solid rgba(124, 92, 255, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <Stack gap={3}>
        <Row gap={2} align="center" justify="between">
          <Row gap={2} align="center">
            <Plug size={14} strokeWidth={2} color="var(--color-accent-soft-text)" />
            <span style={{ fontWeight: 'var(--fw-semibold)' }}>Integration ready</span>
          </Row>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </Row>

        <Stack gap={1}>
          <span className="ds-label">Webhook URL</span>
          <Row gap={2} align="center">
            <code
              className="ds-mono"
              style={{
                flex: 1,
                wordBreak: 'break-all',
                background: 'rgba(0,0,0,0.25)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--fs-xs)',
              }}
            >
              {url}
            </code>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Copy size={12} strokeWidth={2} />}
              onClick={() => copy(url, setUrlCopied)}
            >
              {urlCopied ? 'Copied' : 'Copy'}
            </Button>
          </Row>
        </Stack>

        {provider !== 'sentry' && (
          <Stack gap={1}>
            <span className="ds-label">{secretLabel(provider)}</span>
            <Row gap={2} align="center">
              <code
                className="ds-mono"
                style={{
                  flex: 1,
                  wordBreak: 'break-all',
                  background: 'rgba(0,0,0,0.25)',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--fs-xs)',
                }}
              >
                {secret}
              </code>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Copy size={12} strokeWidth={2} />}
                onClick={() => copy(secret, setSecretCopied)}
              >
                {secretCopied ? 'Copied' : 'Copy'}
              </Button>
            </Row>
          </Stack>
        )}

        <p className="ds-text-tertiary ds-text-xs">{providerInstructions(provider)}</p>
      </Stack>
    </div>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}

function secretLabel(provider: IntegrationProvider): string {
  // Sentry signs payloads with HMAC; the secret is its "Client Secret".
  // Other providers echo our token in a custom header.
  return provider === 'sentry' ? 'Sentry Client Secret' : 'Header — X-Rouser-Secret';
}

function providerInstructions(provider: IntegrationProvider): string {
  switch (provider) {
    case 'sentry':
      return 'Paste this URL as the Webhook URL on your Sentry Internal Integration. Sentry will sign each webhook with the Client Secret you supplied; Rouser verifies the HMAC signature on receipt.';
    default:
      return "Paste both into the provider's webhook configuration. Send the secret in the X-Rouser-Secret header. The secret is persisted on Rouser too, so you can come back later — but treat it like a password.";
  }
}
